import { type KeyboardEvent, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import useWebview from "@/webview/useWebview";
import useTranslations from "@/i18n/useTranslations";

export interface SkillsListProps {
  associatedSkills: string[];
  title?: string;
  theme?: "light" | "dark";
  listClassName?: string;
}

export default function SkillsList({
  associatedSkills,
  title,
  theme = "light",
  listClassName = "",
}: SkillsListProps) {
  const t = useTranslations("Course");
  const { getUrl } = useWebview();
  const skillRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
      return;
    }

    const activeElement = event.target as HTMLElement | null;
    if (!activeElement || activeElement.tagName !== "A") {
      return;
    }

    const currentIndex = skillRefs.current.findIndex(
      (element) => element === activeElement,
    );

    if (currentIndex === -1 || skillRefs.current.length <= 1) {
      return;
    }

    event.preventDefault();

    const direction = event.key === "ArrowRight" ? 1 : -1;
    const total = skillRefs.current.length;
    let nextIndex = currentIndex;

    for (let attempts = 0; attempts < total; attempts += 1) {
      nextIndex = (nextIndex + direction + total) % total;
      const nextElement = skillRefs.current[nextIndex];

      if (nextElement) {
        nextElement.focus();
        break;
      }
    }
  };

  if (!associatedSkills || associatedSkills.length === 0) {
    return null;
  }

  return (
    <div className="flex-shrink-0">
      <h3 className="mt-0 text-xl md:text-2xl">
        {title ?? t("growTheseSkills")}
      </h3>
      <div
        className={cn("flex flex-row flex-wrap gap-2", listClassName)}
        onKeyDown={handleKeyDown}
      >
        {associatedSkills.map((skill, index) => (
          <a
            key={skill}
            href={getUrl(`/search?skills.skill=${skill.replaceAll(" ", "+")}`)}
            ref={(element) => {
              skillRefs.current[index] = element;
            }}
          >
            <Badge
              className="whitespace-nowrap rounded border-[1.5px]"
              variant={theme === "dark" ? "secondary" : "default"}
            >
              {skill}
            </Badge>
          </a>
        ))}
      </div>
    </div>
  );
}
